"use client";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useState, useEffect } from "react";
import {
  ChakraProvider,
  Box,
  Spinner,
  Center,
  ColorModeScript,
  Text,
  VStack,
} from "@chakra-ui/react";
import { supabase } from "./supabaseClient";
import SignIn from "./components/SignIn";
import SignUp from "./components/SignUp";
import OnboardingJourney from "./components/OnboardingJourney";
import Billing from "./components/Billing";
import OTPVerification from "./components/OTPVerification";
import PasswordReset from "./components/PasswordReset";
import PasswordResetOTP from "./components/PasswordResetOTP";
import SetNewPassword from "./components/SetNewPassword";
import PublicRoute from "./components/PublicRoute";
import PrivateRoute from "./components/PrivateRoute";
import { AuthProvider } from "./contexts/AuthContext";
import useAuthVisibilityRefresh from "./hooks/useAuthVisibilityRefresh";
import Layout from "./components/Layout";

import theme from "./theme";
import { API_BASE_URL } from "./config";

function App() {
  const [appLoading, setAppLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  useAuthVisibilityRefresh();

  const addDebugInfo = (info: string) => {
    console.log(`[DEBUG] ${info}`);
    setDebugInfo((prev) => [...prev, `${new Date().toISOString()}: ${info}`]);
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        addDebugInfo("App initialization started");

        addDebugInfo(
          `Supabase URL: ${
            process.env.REACT_APP_SUPABASE_URL ? "Set" : "NOT SET"
          }`
        );
        addDebugInfo(
          `Supabase Anon Key: ${
            process.env.REACT_APP_SUPABASE_ANON_KEY ? "Set" : "NOT SET"
          }`
        );
        addDebugInfo(
          `Server URL: ${API_BASE_URL || "(dev proxy → http://localhost:8000)"}`,
        );

        addDebugInfo("Testing Supabase connection...");
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          addDebugInfo(`Supabase error: ${error.message}`);
          throw error;
        }

        addDebugInfo("Supabase connection successful");
        addDebugInfo(`Session exists: ${data.session ? "Yes" : "No"}`);

        try {
            addDebugInfo("Testing server connection...");
            const response = await fetch(`${API_BASE_URL}/health`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            });

            if (response.ok) {
              addDebugInfo("Server connection successful");
            } else {
              addDebugInfo(`Server responded with status: ${response.status}`);
            }
          } catch (serverError: any) {
            addDebugInfo(`Server connection failed: ${serverError.message}`);
          }

        addDebugInfo("App initialization completed");
        setAppLoading(false);
      } catch (error: any) {
        addDebugInfo(`App initialization failed: ${error.message}`);
        console.error("App initialization error:", error);
        setAppLoading(false);
      }
    };

    initializeApp();
  }, []);

  if (appLoading) {
    return (
      <ChakraProvider theme={theme}>
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="primary.500" />
            <Text>Loading application...</Text>
            {process.env.NODE_ENV === "development" && (
              <Box maxW="md" p={4} bg="gray.100" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold" mb={2}>
                  Debug Info:
                </Text>
                {debugInfo.map((info, index) => (
                  <Text key={index} fontSize="xs" color="gray.600">
                    {info}
                  </Text>
                ))}
              </Box>
            )}
          </VStack>
        </Center>
      </ChakraProvider>
    );
  }

  return (
    <ChakraProvider theme={theme}>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <AuthProvider>
        <Router>
          <Box minH="100vh" w="100%">
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUp />
                  </PublicRoute>
                }
              />
              <Route
                path="/verify"
                element={
                  <PublicRoute>
                    <OTPVerification
                      email=""
                      onVerificationComplete={() => {}}
                      onBackToSignIn={() => {}}
                    />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <PasswordReset />
                  </PublicRoute>
                }
              />
              <Route
                path="/password-reset-otp"
                element={
                  <PublicRoute>
                    <PasswordResetOTP />
                  </PublicRoute>
                }
              />
              <Route
                path="/set-new-password"
                element={
                  <PublicRoute>
                    <SetNewPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/onboarding-form"
                element={
                  <PrivateRoute>
                    <Layout>
                      <OnboardingJourney />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Billing />
                    </Layout>
                  </PrivateRoute>
                }
              />

              <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
          </Box>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
